import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { UserRole } from "../../constants/roles";

export const selectAuthState = (state: RootState) => state.auth;

export const selectUser = (state: RootState) => state.auth.user;
export const selectToken = (state: RootState) => state.auth.token;
export const selectIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState) => state.auth.isLoading;
export const selectAuthError = (state: RootState) => state.auth.error;

export const selectHasUserData = createSelector(
  [selectUser],
  (user) => user && user.id && user.email
);

export const selectUserRole = createSelector(
  [selectUser],
  (user) => user?.roleTitle || null
);

export const selectUserDepartments = createSelector(
  [selectUser],
  (user) => user?.departments || []
);

export const selectUserFullName = createSelector([selectUser], (user) =>
  user ? `${user.firstName} ${user.lastName}`.trim() : null
);

export const selectUserInitials = createSelector([selectUser], (user) => {
  if (!user) return null;
  const firstName = user.firstName || "";
  const lastName = user.lastName || "";
  return `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();
});

export const selectUserFirstName = createSelector(
  [selectUser],
  (user) => user?.firstName || null
);

export const selectUserLastName = createSelector(
  [selectUser],
  (user) => user?.lastName || null
);

export const selectUserEmail = createSelector(
  [selectUser],
  (user) => user?.email || null
);

export const selectUserPhoneNumber = createSelector(
  [selectUser],
  (user) => user?.phoneNumber || null
);

export const selectUserDateOfBirth = createSelector(
  [selectUser],
  (user) => user?.dateOfBirth || null
);

export const selectUserRoleId = createSelector(
  [selectUser],
  (user) => user?.roleId || null
);

export const selectUserStatus = createSelector(
  [selectUser],
  (user) => user?.status || null
);

export const selectIsManager = createSelector(
  [selectUserRoleId],
  (roleId) => roleId === UserRole.Manager
);

export const selectIsAssistant = createSelector(
  [selectUserRoleId],
  (roleId) => roleId === UserRole.Assistant
);

export const selectIsProvider = createSelector(
  [selectUserRoleId],
  (roleId) => roleId === UserRole.Provider
);

export const selectIsSuperAdmin = createSelector(
  [selectUserRoleId],
  (roleId) => roleId === UserRole.SuperAdmin
);

/** @deprecated Use selectIsSuperAdmin */
export const selectIsSuperadmin = selectIsSuperAdmin;

export const selectRegistrationToken = (state: RootState) =>
  state.auth.registrationToken;

export const selectAuthStatus = createSelector(
  [selectIsAuthenticated, selectAuthLoading, selectAuthError],
  (isAuthenticated, isLoading, error) => ({
    isAuthenticated,
    isLoading,
    error,
    isLoggedIn: isAuthenticated && !isLoading,
  })
);
